import { useMemo } from 'react';
import { History, ArrowRight, PlusCircle, Pencil } from 'lucide-react';
import type { RevenueTransition } from '../../types/finance';
import { revenueFieldLabel } from '../../lib/api/mappers';
import { Spinner } from '../ui/Spinner';
import { formatDateTime } from '../../utils/trips';
import { formatearCOP } from '../../utils/expenses';
import { formatearCantidad } from '../../utils/finance';
import { useCustomers } from '../../hooks/useCustomers';
import { useVehicles } from '../../hooks/useVehicles';
import { useDrivers } from '../../hooks/useDrivers';
import { nombreDeActor } from '../../utils/actor';

function resolverValor(
  field: string,
  valor: string,
  maps: { clientes: Map<string, string>; vehiculos: Map<string, string>; conductores: Map<string, string> },
): { texto: string; title?: string } {
  if (!valor) return { texto: '—' };
  if ((field === 'amount' || field === 'computedAmount') && /^\d+(\.\d{1,2})?$/.test(valor)) return { texto: formatearCOP(valor) };
  if (field === 'quantity') return { texto: formatearCantidad(valor) };
  if (field === 'customerId') return { texto: maps.clientes.get(valor) ?? valor.slice(0, 8), title: valor };
  if (field === 'vehicleId') return { texto: maps.vehiculos.get(valor) ?? valor.slice(0, 8), title: valor };
  if (field === 'driverId') return { texto: maps.conductores.get(valor) ?? valor.slice(0, 8), title: valor };
  if (field === 'occurredAt') return { texto: formatDateTime(valor) };
  if (field === 'tariffId' || field === 'tripId') return { texto: valor.slice(0, 8), title: valor };
  return { texto: valor };
}

/**
 * FE-32 — la bitácora de un ingreso. Igual que la del gasto, con una
 * diferencia que importa: el **registro inicial** puede traer un cambio de
 * `amount`. Significa que lo cobrado no fue lo que la tarifa propuso
 * (`from` = propuesto, `to` = cobrado) y la razón de abajo explica por qué.
 * Es el defecto del cuaderno —el mínimo con dos valores— hecho visible.
 */
export function RevenueTransitions({ transitions, isLoading, error }: {
  transitions: RevenueTransition[];
  isLoading: boolean;
  error: Error | null;
}) {
  const { customers } = useCustomers();
  const { vehicles } = useVehicles();
  const { drivers } = useDrivers();
  const maps = useMemo(() => ({
    clientes: new Map(customers.map(c => [c.id, c.name])),
    vehiculos: new Map(vehicles.map(v => [v.id, v.plate])),
    conductores: new Map(drivers.map(d => [d.id, d.name])),
  }), [customers, vehicles, drivers]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <h4 style={{ margin: 0, fontSize: '13px', fontWeight: 800, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
        <History size={15} style={{ color: 'var(--accent)' }} />
        Bitácora del ingreso
      </h4>
      {isLoading && <div style={{ padding: '1rem', display: 'flex', justifyContent: 'center' }}><Spinner /></div>}
      {!isLoading && error && <p style={{ margin: 0, fontSize: '12px', color: '#EF4444' }}>No se pudo cargar la bitácora: {error.message}</p>}
      {!isLoading && !error && transitions.length === 0 && <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)' }}>Sin anotaciones.</p>}
      {!isLoading && !error && transitions.length > 0 && (
        <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {transitions.map((t, i) => {
            const esCorreccion = t.action === 'CORRECTED';
            const conOverride = !esCorreccion && t.changes.some(c => c.field === 'amount');
            const color = esCorreccion ? '#F59E0B' : conOverride ? '#8B5CF6' : '#10B981';
            return (
              <li key={`${t.recordedAt}-${i}`} style={{ background: 'var(--bg-raised)', border: '1px solid var(--border)', borderLeft: `3px solid ${color}`, borderRadius: 'var(--radius-md)', padding: '0.625rem 0.875rem', display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '12px', fontWeight: 800, color }}>
                  {esCorreccion ? <Pencil size={12} /> : <PlusCircle size={12} />}
                  {esCorreccion ? 'Corrección' : conOverride ? 'Registro con importe distinto del propuesto' : 'Registro inicial'}
                </div>
                {t.changes.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                    {t.changes.map(c => {
                      const desde = resolverValor(c.field, c.from, maps);
                      const hasta = resolverValor(c.field, c.to, maps);
                      const etiqueta = !esCorreccion && c.field === 'amount' ? 'Propuesto → cobrado' : revenueFieldLabel(c.field);
                      return (
                        <div key={c.field} style={{ fontSize: '11.5px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'baseline', gap: '0.375rem', flexWrap: 'wrap' }}>
                          <strong style={{ color: 'var(--text-primary)', fontWeight: 700 }}>{etiqueta}:</strong>
                          <span style={{ textDecoration: esCorreccion ? 'line-through' : 'none', opacity: 0.75 }} title={desde.title}>{desde.texto}</span>
                          <ArrowRight size={11} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                          <span style={{ fontWeight: 700, color: 'var(--text-primary)' }} title={hasta.title}>{hasta.texto}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
                {t.reason && <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>“{t.reason}”</p>}
                <span style={{ fontSize: '11px', color: 'var(--text-muted)' }} title={t.actor}>
                  {formatDateTime(t.recordedAt)} · {nombreDeActor(t.actor, t.actorName)}
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
